import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Trash2, Save, Plus } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function MenuTab() {
  const { API, authHeaders, logout } = useAuth();
  const [sections, setSections] = useState([]);
  const [edits, setEdits] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [form, setForm] = useState({ section: "", newTitle: "", name: "", note: "", half: "", full: "" });
  const [adding, setAdding] = useState(false);

  const onErr = useCallback((e) => { if (e.response?.status === 401) { toast.error("Session expired"); logout(); } }, [logout]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/menu`);
      setSections(data);
      const e = {};
      data.forEach((sec) => sec.items.forEach((it) => { e[it.id] = { name: it.name, note: it.note || "", half: it.half, full: it.full }; }));
      setEdits(e);
    } catch (e) { onErr(e); } finally { setLoading(false); }
  }, [API, onErr]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const change = (id, k, v) => setEdits((prev) => ({ ...prev, [id]: { ...prev[id], [k]: v } }));

  const save = async (id) => {
    setSaving(id);
    try {
      const e = edits[id];
      await axios.patch(`${API}/admin/menu/${id}`, { name: e.name, note: e.note, half: Number(e.half), full: Number(e.full) }, { headers: authHeaders });
      toast.success("Saved");
    } catch (err) { onErr(err); toast.error("Could not save"); } finally { setSaving(null); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove "${name}" from the menu?`)) return;
    try {
      await axios.delete(`${API}/admin/menu/${id}`, { headers: authHeaders });
      toast.success("Removed");
      load();
    } catch (err) { onErr(err); toast.error("Could not remove"); }
  };

  const addItem = async (e) => {
    e.preventDefault();
    const isNew = form.section === "__new__";
    const section_title = isNew ? form.newTitle.trim() : (sections.find((s) => s.id === form.section)?.title || "");
    const section_id = isNew ? slugify(form.newTitle) : form.section;
    if (!section_id || !section_title || !form.name.trim() || form.half === "" || form.full === "") {
      toast.error("Fill section, dish name, and both prices.");
      return;
    }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/menu`, {
        section_id, section_title, name: form.name.trim(), note: form.note.trim() || null,
        half: Number(form.half), full: Number(form.full),
      }, { headers: authHeaders });
      toast.success("Dish added");
      setForm({ section: form.section, newTitle: "", name: "", note: "", half: "", full: "" });
      load();
    } catch (err) { onErr(err); toast.error("Could not add dish"); } finally { setAdding(false); }
  };

  const inp = "rounded-lg border border-nosh-border bg-white px-3 py-2 font-body text-sm text-nosh-ink outline-none focus:border-nosh-green";

  if (loading) return <div className="grid place-items-center rounded-2xl border border-nosh-border bg-white py-20"><Loader2 className="h-6 w-6 animate-spin text-nosh-green" /></div>;

  return (
    <div className="space-y-6">
      {/* Add dish */}
      <form onSubmit={addItem} data-testid="menu-add-form" className="rounded-2xl border-2 border-dashed border-nosh-green/40 bg-white p-5">
        <h3 className="mb-3 font-display text-lg font-bold text-nosh-ink">Add a dish</h3>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
          <select data-testid="menu-add-section" className={`${inp} lg:col-span-2`} value={form.section} onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))}>
            <option value="">Section…</option>
            {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            <option value="__new__">➕ New section…</option>
          </select>
          {form.section === "__new__" && (
            <input className={`${inp} lg:col-span-2`} placeholder="New section name" value={form.newTitle} onChange={(e) => setForm((f) => ({ ...f, newTitle: e.target.value }))} data-testid="menu-add-newsection" />
          )}
          <input data-testid="menu-add-name" className={`${inp} lg:col-span-2`} placeholder="Dish name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <input className={`${inp} lg:col-span-2`} placeholder="Note (optional)" value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
          <input data-testid="menu-add-half" type="number" className={inp} placeholder="Half $" value={form.half} onChange={(e) => setForm((f) => ({ ...f, half: e.target.value }))} />
          <input data-testid="menu-add-full" type="number" className={inp} placeholder="Full $" value={form.full} onChange={(e) => setForm((f) => ({ ...f, full: e.target.value }))} />
          <button type="submit" disabled={adding} data-testid="menu-add-submit" className="flex items-center justify-center gap-2 rounded-lg bg-nosh-green px-4 py-2 font-poster text-xs font-bold uppercase tracking-wide text-nosh-cream transition-colors hover:bg-nosh-ink disabled:opacity-60 lg:col-span-2">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add dish
          </button>
        </div>
      </form>

      {/* Sections */}
      {sections.map((sec) => (
        <div key={sec.id} className="overflow-hidden rounded-2xl border border-nosh-border bg-white" data-testid={`menu-section-${sec.id}`}>
          <div className="border-b border-nosh-border bg-nosh-surface px-5 py-3">
            <h3 className="font-display text-xl font-bold text-nosh-ink">{sec.title}</h3>
          </div>
          <div className="divide-y divide-nosh-border/60">
            {sec.items.map((it) => {
              const e = edits[it.id] || {};
              return (
                <div key={it.id} data-testid={`menu-item-${it.id}`} className="grid grid-cols-1 gap-2 px-5 py-3 sm:grid-cols-[1.6fr_1.6fr_0.7fr_0.7fr_auto] sm:items-center">
                  <input className={inp} value={e.name || ""} onChange={(ev) => change(it.id, "name", ev.target.value)} data-testid={`menu-name-${it.id}`} />
                  <input className={inp} value={e.note || ""} placeholder="Note" onChange={(ev) => change(it.id, "note", ev.target.value)} />
                  <div className="flex items-center gap-1"><span className="font-poster text-[10px] uppercase text-nosh-ink/40">Half</span><input type="number" className={`${inp} w-full`} value={e.half ?? ""} onChange={(ev) => change(it.id, "half", ev.target.value)} data-testid={`menu-half-${it.id}`} /></div>
                  <div className="flex items-center gap-1"><span className="font-poster text-[10px] uppercase text-nosh-ink/40">Full</span><input type="number" className={`${inp} w-full`} value={e.full ?? ""} onChange={(ev) => change(it.id, "full", ev.target.value)} data-testid={`menu-full-${it.id}`} /></div>
                  <div className="flex gap-2">
                    <button onClick={() => save(it.id)} disabled={saving === it.id} data-testid={`menu-save-${it.id}`} className="grid h-9 w-9 place-items-center rounded-full bg-nosh-green text-nosh-cream transition-colors hover:bg-nosh-ink disabled:opacity-60" title="Save">{saving === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}</button>
                    <button onClick={() => remove(it.id, it.name)} data-testid={`menu-delete-${it.id}`} className="grid h-9 w-9 place-items-center rounded-full border border-nosh-border text-nosh-ink/60 transition-colors hover:border-red-400 hover:text-red-500" title="Remove"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
